import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { History } from 'lucide-react';
import { ToolbarIconButton } from './ToolbarIconButton';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useSnapshot } from 'valtio';
import { djs } from '@duke-hq/djs';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { toast } from '@duke-hq/app-admin-web/components/ToastContainer';
import { flowEditorState } from '../components/ReactFlow/flowEditorState';

// Matches the format the published badge uses, so a version's date reads the
// same wherever it appears in the editor chrome.
const DATE_FORMAT = 'D MMM YYYY, h:mma';

// The minimal shape of a published version the popover row renders. Mirrors the
// `resourceVersions.listPublished` query output (id, version, published_at), so
// the presentational view and the live wrapper agree on one definition and the
// view renders in Storybook from synthetic data without the query.
export type PublishedVersionRow = {
  id: number;
  version: number;
  published_at: Date | null;
};

// A restore request from a row, raised to the caller so the wrapper can gate it
// behind the destructive confirm. The view itself does no restoring.
export type VersionRestoreRequest = {
  versionId: number;
  version: number;
};

// Restore reloads the page, which would wipe a toast fired straight after the
// mutation. Stash the restored version under a per-tab, resource-scoped key so
// the reloaded page can both confirm the load (toast) and show which version
// the draft was restored from. sessionStorage (not localStorage) matches the
// claim's real lifetime: this editing session, this tab.
const restoredBreadcrumbKey = (resourceId: string) => `flow-version-restored:${resourceId}`;

type RestoredBreadcrumb = {
  // The published version number the draft was restored from (display value).
  version: number;
  // The draft revision immediately after the restore. If the live draft has
  // since advanced past this, the draft has been saved/diverged (possibly in
  // another tab) and the breadcrumb is stale, so we drop it.
  restoreRevision: number;
};

function readRestoredBreadcrumb(resourceId: string): RestoredBreadcrumb | null {
  const raw = sessionStorage.getItem(restoredBreadcrumbKey(resourceId));
  if (raw === null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'version' in parsed &&
      'restoreRevision' in parsed &&
      typeof parsed.version === 'number' &&
      typeof parsed.restoreRevision === 'number'
    ) {
      return { version: parsed.version, restoreRevision: parsed.restoreRevision };
    }
  } catch {
    // Corrupt value: fall through and drop it below.
  }
  sessionStorage.removeItem(restoredBreadcrumbKey(resourceId));
  return null;
}

// Wraps FlowVersionHistory for the conversation editor. Owns the flowEditorState
// subscription here rather than in FlowEditor, which never subscribes to the
// store (a snapshot there would re-render the canvas on every edit).
export function ConversationVersionHistory(
  props: Omit<Parameters<typeof FlowVersionHistory>[0], 'draftIsDirty'>,
) {
  const { isDirty } = useSnapshot(flowEditorState);
  return <FlowVersionHistory {...props} draftIsDirty={isDirty} />;
}

/**
 * The content editors' version-history entry point: a History action that opens
 * a popover listing the resource's published versions newest-first, plus the
 * current working draft as a non-selectable row at the top so the author can
 * see what they are editing against what is live. Shared by the conversation
 * and unit-guide editors, each passing its own `draftIsDirty`.
 *
 * Restoring a version copies that published snapshot back into the draft (it does
 * not publish, so students are unaffected) and reloads the page, because the
 * editor seeds its content once on mount; this mirrors the stale-tab "load
 * latest" recovery. When the draft has unsaved edits the restore is gated behind
 * a destructive confirm, since the reload discards them.
 *
 * Thin wrapper over the presentational `FlowVersionHistoryView`: it owns every
 * bit of wiring (the listPublished query refetched on open, the restore
 * mutation, the sessionStorage restore breadcrumb + reload, the restore-on-reload
 * toast, and the destructive confirm dialog) and hands the view plain data plus
 * an `onRestore` that raises the confirm. The view holds all the markup, so the
 * whole popover renders in Storybook without tRPC.
 */
export function FlowVersionHistory({
  resourceId,
  draftVersionId,
  draftRevision,
  livePublishedVersion,
  draftIsDirty,
  getBaseRevision,
}: {
  resourceId: string;
  draftVersionId: number | undefined;
  draftRevision: number;
  livePublishedVersion: number | null | undefined;
  // Passed in (not read from a store) because the two editors track dirtiness
  // differently; gates a restore-over-unsaved-work behind the destructive confirm.
  draftIsDirty: boolean;
  getBaseRevision: () => number;
}) {
  const [pendingRestore, setPendingRestore] = useState<VersionRestoreRequest | null>(null);
  // The published version the draft was restored from this session, if any. An
  // origin claim, not an identity claim: it stays true after the author edits
  // (the adjacent "Unsaved changes" indicator signals divergence).
  const [restoredFromVersion, setRestoredFromVersion] = useState<number | null>(null);

  // After a restore reload, surface which version was loaded: confirm it once
  // with a toast, and keep it for the session so the Current draft row can show
  // where the draft came from.
  useEffect(() => {
    const breadcrumb = readRestoredBreadcrumb(resourceId);
    if (breadcrumb === null) {
      return;
    }
    // If the draft has advanced past the restore point, it has been saved or
    // changed since (possibly in another tab); the breadcrumb no longer
    // describes the current content, so drop it.
    if (draftRevision > breadcrumb.restoreRevision) {
      sessionStorage.removeItem(restoredBreadcrumbKey(resourceId));
      return;
    }
    sessionStorage.removeItem(restoredBreadcrumbKey(resourceId));
    setRestoredFromVersion(breadcrumb.version);
    toast({
      status: 'success',
      title: t`Version ${breadcrumb.version} restored to draft`,
    });
  }, [resourceId, draftRevision]);

  const publishedVersions = trpcAdmin.resourceVersions.listPublished.useQuery(
    { resourceId },
    // Refetched each time the popover opens via the trigger below, so the list
    // reflects any publishes made since mount without polling.
    { enabled: false },
  );

  const restore = trpcAdmin.resourceVersions.restore.useMutation();

  const runRestore = async () => {
    if (!pendingRestore || !draftVersionId) {
      return;
    }
    try {
      const restored = await restore.mutateAsync({
        resourceVersionId: draftVersionId,
        fromVersionId: pendingRestore.versionId,
        baseRevision: getBaseRevision(),
      });
      // Leave a breadcrumb for the reloaded page to confirm the load (toast) and
      // show the draft's origin. restoreRevision is the draft revision AFTER the
      // restore (the mutation bumps it), so the mount-time staleness check only
      // fires once a later save advances past it. Storage failures (e.g. private
      // mode) must not abort the reload that shows the restored content.
      try {
        const breadcrumb: RestoredBreadcrumb = {
          version: pendingRestore.version,
          restoreRevision: restored.revision,
        };
        sessionStorage.setItem(restoredBreadcrumbKey(resourceId), JSON.stringify(breadcrumb));
      } catch {
        // No breadcrumb this time; the restore itself still applied.
      }
      // The editor seeds content once on mount, so reload to re-hydrate the
      // canvas from the restored draft and reset the tracked revision. Defer by
      // one tick so the sessionStorage write above is guaranteed to flush before
      // the page tears down.
      setTimeout(() => window.location.reload(), 0);
    } catch {
      // A stale draft yields a CONFLICT; the stale-editor affordance handles
      // recovery, so here we just surface that the restore did not apply.
      toast({
        status: 'error',
        title: t`Could not restore version`,
        description: t`The draft has changed since this list was loaded. Reload and try again.`,
      });
      setPendingRestore(null);
    }
  };

  return (
    <>
      <FlowVersionHistoryView
        publishedVersions={publishedVersions.data}
        isFetchingVersions={publishedVersions.isFetching}
        draftIsDirty={draftIsDirty}
        restoredFromVersion={restoredFromVersion}
        livePublishedVersion={livePublishedVersion}
        onOpen={() => publishedVersions.refetch()}
        onRestore={setPendingRestore}
      />

      <ConfirmationDialog
        isOpen={pendingRestore !== null}
        onClose={() => setPendingRestore(null)}
        variant={draftIsDirty ? 'destructive' : 'default'}
        title={t`Restore this version?`}
        body={
          pendingRestore
            ? [
                t`This will replace your current draft with the content from version ${pendingRestore.version}.`,
                draftIsDirty ? t`Your unsaved changes will be discarded.` : '',
                t`Students keep seeing the live version until you publish again.`,
              ]
                .filter(Boolean)
                .join(' ')
            : ''
        }
        confirmLabel={t`Restore to draft`}
        isLoading={restore.isPending}
        onConfirm={runRestore}
      />
    </>
  );
}

/**
 * The presentational body of the version-history popover: the History trigger,
 * the popover header, a pinned "Current draft" row (with the optional "Restored
 * from version N" origin line and an "Unsaved changes" indicator), then the
 * published-versions list newest-first (each row: version number, a "Live"
 * marker on the currently-live version, the published date, and a Restore button
 * revealed on hover), plus a loading spinner and an empty state.
 *
 * Pure: no hooks beyond render, no tRPC, no sessionStorage. The caller supplies
 * the data and an `onRestore` raised when a row's Restore is pressed (the
 * destructive confirm and the reload live in the wrapper), so the popover renders
 * in Storybook without a provider.
 */
export function FlowVersionHistoryView({
  publishedVersions,
  isFetchingVersions,
  draftIsDirty,
  restoredFromVersion,
  livePublishedVersion,
  onOpen,
  onRestore,
}: {
  publishedVersions: PublishedVersionRow[] | undefined;
  isFetchingVersions: boolean;
  draftIsDirty: boolean;
  restoredFromVersion: number | null;
  livePublishedVersion: number | null | undefined;
  /** Called when the popover opens, so the caller can refetch the list. */
  onOpen?: () => void;
  /** Called when a published version's Restore is pressed. */
  onRestore?: (request: VersionRestoreRequest) => void;
}) {
  return (
    <Popover placement="bottom-end" onOpen={() => onOpen?.()}>
      <PopoverTrigger>
        <Box>
          <Tooltip label={t`Version history`}>
            <ToolbarIconButton aria-label={t`Version history`}>
              <History size={iconSize.md} />
            </ToolbarIconButton>
          </Tooltip>
        </Box>
      </PopoverTrigger>
      <PopoverContent width={pxToRem(320)}>
        <PopoverArrow />
        <PopoverHeader fontSize="sm" fontWeight="semibold">
          <Trans>Version history</Trans>
        </PopoverHeader>
        <PopoverBody p={0}>
          <Box px={3} py={2} borderBottomWidth="1px" borderColor="border.default">
            <HStack justify="space-between" align="start">
              <Box>
                <Text fontSize="sm" fontWeight="medium">
                  <Trans>Current draft</Trans>
                </Text>
                {restoredFromVersion != null ? (
                  <Text fontSize="xs" color="fg.muted">
                    {restoredFromVersion === livePublishedVersion ? (
                      <Trans>Restored from version {restoredFromVersion} (currently live)</Trans>
                    ) : (
                      <Trans>Restored from version {restoredFromVersion}</Trans>
                    )}
                  </Text>
                ) : null}
              </Box>
              {draftIsDirty ? (
                <Text fontSize="xs" color="fg.muted" flexShrink={0}>
                  <Trans>Unsaved changes</Trans>
                </Text>
              ) : null}
            </HStack>
          </Box>

          {isFetchingVersions ? (
            <Flex justify="center" py={4}>
              <Spinner size="sm" />
            </Flex>
          ) : publishedVersions && publishedVersions.length > 0 ? (
            // Cap the list so a long history scrolls within the popover rather
            // than running off the bottom of the canvas and pushing the header
            // out of view. The draft row above stays pinned.
            <VStack align="stretch" spacing={0} maxH={pxToRem(320)} overflowY="auto">
              {publishedVersions.map((version) => (
                <HStack
                  key={version.id}
                  role="group"
                  justify="space-between"
                  px={3}
                  py={2}
                  borderBottomWidth="1px"
                  borderColor="border.default"
                  _last={{ borderBottomWidth: 0 }}
                  _hover={{ bg: 'bg.subtle' }}
                >
                  <Box>
                    <HStack spacing={2}>
                      <Text fontSize="sm" fontWeight="medium">
                        <Trans>Version {version.version}</Trans>
                      </Text>
                      {version.version === livePublishedVersion ? (
                        <Text fontSize="xs" fontWeight="semibold" color="fg.accent.default">
                          <Trans>Live</Trans>
                        </Text>
                      ) : null}
                    </HStack>
                    {version.published_at ? (
                      <Text fontSize="xs" color="fg.muted">
                        {djs(version.published_at).format(DATE_FORMAT)}
                      </Text>
                    ) : null}
                  </Box>
                  <Button
                    size="xs"
                    variant="secondary"
                    opacity={0}
                    _groupHover={{ opacity: 1 }}
                    _groupFocusWithin={{ opacity: 1 }}
                    onClick={() => onRestore?.({ versionId: version.id, version: version.version })}
                  >
                    <Trans>Restore</Trans>
                  </Button>
                </HStack>
              ))}
            </VStack>
          ) : (
            <Box px={3} py={4}>
              <Text fontSize="sm" color="fg.muted">
                <Trans>No published versions yet.</Trans>
              </Text>
            </Box>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
